import type {ComboboxItem} from '@mantine/core';
import {
  EqualityOperatorEnum,
  FormStateEnum,
  InspectionStateEnum,
  NotificationRuleChangeConditionPropertyEnum,
  NotificationRuleChangeTypeEnum,
  NotificationRuleCourseChangeTypeEnum,
  UserStatusEnum,
  UserTypeEnum,
  RoleEnum,
  CourseAssignmentStateEnum,
  CourseSessionStateEnum,
  CourseSessionContentStateEnum,
  TaskAssignmentStateEnum,
  ItemAssignmentStateEnum,
  SafetyEventStateEnum,
} from 'src/api/ProactApiClient';
import {attachmentModuleEnumOptions} from 'src/app/attachments/attachmentModuleEnumOptions';
import type {ChangeEventModuleConfig} from './changeEventConfig.types';
import {
  AttachmentChangeConditionPropertyOptions,
  PersonnelChangeConditionPropertyOptions,
  PersonnelChangeOperationOptions,
  UserStatusOptions,
  UserTypeOptions,
  RoleOptions,
  BooleanYesNoOptions,
  BooleanMarkerOptions,
  TaskAssignmentChangeConditionPropertyOptions,
  TaskAssignmentStateOptions,
  ItemAssignmentChangeConditionPropertyOptions,
  ItemAssignmentStateOptions,
  SafetyEventChangeConditionPropertyOptions,
  SafetyEventStateOptions,
  InspectionChangeConditionPropertyOptions,
  InspectionStateOptions,
  FormChangeConditionPropertyOptions,
  FormStateOptions,
  CourseAssignmentChangeConditionPropertyOptions,
  CourseAssignmentStateOptions,
  CourseAssignmentEnrollmentOptions,
  CourseSessionChangeConditionPropertyOptions,
  CourseSessionStateOptions,
  CourseContentChangeConditionPropertyOptions,
  CourseContentStateOptions,
} from '../notificationRuleHelpers';

export const PersonnelChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'personnel',
  conditionsFieldName: 'personnelChangeConditions',
  propertyOptions: PersonnelChangeConditionPropertyOptions,
  defaultProperty:
    NotificationRuleChangeConditionPropertyEnum.PersonnelOperationType,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.PersonnelOperationType,
      sourceType: 'static',
      options: PersonnelChangeOperationOptions,
      defaultValue: '1', // Created
      description:
        'Fires when a personnel record is created, updated, or deleted.',
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: UserStatusOptions,
      defaultValue: String(UserStatusEnum.Active),
      description:
        "Fires when a user's account status changes (Active, Inactive, Pending).",
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.PersonnelUserType,
      sourceType: 'static',
      options: UserTypeOptions,
      defaultValue: String(UserTypeEnum.Employee),
      description:
        "Fires when a user's type changes between Employee and Contractor.",
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.PersonnelRole,
      sourceType: 'static',
      options: RoleOptions,
      defaultValue: String(RoleEnum.InstanceUser),
      description: "Fires when a user's assigned role changes.",
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.PersonnelSuspended,
      sourceType: 'static',
      options: BooleanYesNoOptions,
      defaultValue: 'true',
      description: 'Fires when a user is suspended or un-suspended.',
    },
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.PersonnelLeaveOfAbsence,
      sourceType: 'static',
      options: BooleanYesNoOptions,
      defaultValue: 'true',
      description:
        'Fires when a user is placed on or returns from leave of absence.',
    },
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.PersonnelEligibility,
      sourceType: 'static',
      options: BooleanYesNoOptions,
      defaultValue: 'true',
      description: "Fires when a user's eligibility flag changes.",
    },
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.PersonnelBannedOnProperty,
      sourceType: 'static',
      options: BooleanYesNoOptions,
      defaultValue: 'true',
      description: "Fires when a user's banned on property flag changes.",
    },
  ],
};

export const TaskAssignmentChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'taskAssignment',
  conditionsFieldName: 'taskAssignmentChangeConditions',
  propertyOptions: TaskAssignmentChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: TaskAssignmentStateOptions,
      defaultValue: String(TaskAssignmentStateEnum.Assigned),
      description:
        'Fires when the task assignment status changes (Assigned, Completed, Submitted, etc.).',
    },
  ],
};

export const ItemAssignmentChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'itemAssignment',
  conditionsFieldName: 'itemAssignmentChangeConditions',
  propertyOptions: ItemAssignmentChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: ItemAssignmentStateOptions,
      defaultValue: String(ItemAssignmentStateEnum.Assigned),
      description:
        'Fires when an item tracker assignment moves to a new status.',
    },
  ],
};

export const SafetyEventChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'safetyEvent',
  conditionsFieldName: 'safetyEventChangeConditions',
  propertyOptions: SafetyEventChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: SafetyEventStateOptions,
      defaultValue: String(SafetyEventStateEnum.Assemble),
      description:
        'Fires when the safety event moves to a new status (e.g. Assemble → Published).',
    },
  ],
};

// AssigneeChanged fires on any assign/reassign, regardless of who it changed to/from.
export const InspectionChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'inspection',
  conditionsFieldName: 'inspectionChangeConditions',
  propertyOptions: InspectionChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: InspectionStateOptions,
      defaultValue: String(InspectionStateEnum.Completed),
      description:
        'Fires when the inspection moves to a new status (Draft, In Progress, Completed).',
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.AssigneeChanged,
      sourceType: 'static',
      options: BooleanMarkerOptions,
      defaultValue: 'true',
      description: 'Fires whenever the inspection assignee changes.',
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.InspectionGenerated,
      sourceType: 'static',
      options: BooleanMarkerOptions,
      defaultValue: 'true',
      description:
        'Fires when a recurrence schedule generates a new inspection. Generated inspections have no assignee, so target user groups.',
      advisory:
        'Schedule-generated inspections have no assignee or creator, so "Inspection Generated" rules must target user groups — assignee/supervisor recipients resolve to no one.',
    },
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.InspectionScheduleChanged,
      sourceType: 'static',
      options: BooleanMarkerOptions,
      defaultValue: 'true',
      description:
        "Fires when a committed inspection's scheduled start changes without a change of assignee.",
    },
  ],
};

export const FormChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'form',
  conditionsFieldName: 'formChangeConditions',
  propertyOptions: FormChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  usesFormChangeCondition: true,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: FormStateOptions,
      defaultValue: String(FormStateEnum.Published),
      description:
        'Fires when the form moves to a new state (Draft, Published, Failed).',
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.InputValueChange,
      sourceType: 'text',
      defaultValue: '',
      description:
        'Fires when the selected form field is updated to the specified value.',
    },
  ],
};

export const CourseAssignmentChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'courseAssignment',
  conditionsFieldName: 'courseAssignmentChangeConditions',
  propertyOptions: CourseAssignmentChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: CourseAssignmentStateOptions,
      defaultValue: String(CourseAssignmentStateEnum.Complete),
      description:
        'Fires when the course assignment status changes (Open, Complete, Locked, etc.).',
    },
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.TrainingEnrollmentChange,
      sourceType: 'static',
      options: CourseAssignmentEnrollmentOptions,
      defaultValue: 'true',
      description:
        'Fires when a user is enrolled in or removed from the course.',
    },
  ],
};

export const CourseSessionChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'courseSession',
  conditionsFieldName: 'courseSessionChangeConditions',
  propertyOptions: CourseSessionChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: CourseSessionStateOptions,
      defaultValue: String(CourseSessionStateEnum.NotStarted),
      description:
        "Fires when a user's progress through a course session changes (e.g. Not Started → In Progress).",
    },
  ],
};

export const CourseContentChangeConfig: ChangeEventModuleConfig = {
  moduleKey: 'courseContent',
  conditionsFieldName: 'courseContentChangeConditions',
  propertyOptions: CourseContentChangeConditionPropertyOptions,
  defaultProperty: NotificationRuleChangeConditionPropertyEnum.StatusChange,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property: NotificationRuleChangeConditionPropertyEnum.StatusChange,
      sourceType: 'static',
      options: CourseContentStateOptions,
      defaultValue: String(CourseSessionContentStateEnum.NotStarted),
      description:
        'Fires when a specific piece of course content is started or completed.',
    },
  ],
};

export const buildAttachmentChangeConfig = (
  classificationOptions: ComboboxItem[],
  attachmentTypeOptions: ComboboxItem[]
): ChangeEventModuleConfig => ({
  moduleKey: 'attachment',
  conditionsFieldName: 'attachmentChangeConditions',
  propertyOptions: AttachmentChangeConditionPropertyOptions,
  defaultProperty:
    NotificationRuleChangeConditionPropertyEnum.AttachmentClassification,
  defaultOperator: EqualityOperatorEnum.EqualTo,
  valueConfigs: [
    {
      property:
        NotificationRuleChangeConditionPropertyEnum.AttachmentClassification,
      sourceType: 'static',
      options: classificationOptions,
      defaultValue:
        classificationOptions.length > 0
          ? classificationOptions[0].value
          : '',
      description:
        'Fires when an attachment with the selected classification is created or updated.',
    },
    {
      property: NotificationRuleChangeConditionPropertyEnum.AttachmentType,
      sourceType: 'static',
      options: attachmentTypeOptions,
      defaultValue:
        attachmentTypeOptions.length > 0
          ? attachmentTypeOptions[0].value
          : '',
      description:
        'Fires when an attachment of the selected type is created or updated.',
    },
  ],
});

export const getConfigForChangeType = (
  changeType: NotificationRuleChangeTypeEnum,
  courseChangeType?: NotificationRuleCourseChangeTypeEnum,
  attachmentClassificationOptions?: ComboboxItem[],
  attachmentTypeOptions?: ComboboxItem[]
): ChangeEventModuleConfig | null => {
  switch (changeType) {
    case NotificationRuleChangeTypeEnum.PersonnelChange:
      return PersonnelChangeConfig;
    case NotificationRuleChangeTypeEnum.TaskAssignmentChange:
      return TaskAssignmentChangeConfig;
    case NotificationRuleChangeTypeEnum.ItemAssignmentChange:
      return ItemAssignmentChangeConfig;
    case NotificationRuleChangeTypeEnum.SafetyEventChange:
      return SafetyEventChangeConfig;
    case NotificationRuleChangeTypeEnum.InspectionChange:
      return InspectionChangeConfig;
    case NotificationRuleChangeTypeEnum.FormChange:
      return FormChangeConfig;
    case NotificationRuleChangeTypeEnum.CourseChange:
      switch (courseChangeType) {
        case NotificationRuleCourseChangeTypeEnum.CourseAssignmentChange:
          return CourseAssignmentChangeConfig;
        case NotificationRuleCourseChangeTypeEnum.CourseSessionChange:
          return CourseSessionChangeConfig;
        case NotificationRuleCourseChangeTypeEnum.CourseContentChange:
          return CourseContentChangeConfig;
        default:
          return null;
      }
    case NotificationRuleChangeTypeEnum.AttachmentCreated:
    case NotificationRuleChangeTypeEnum.AttachmentUpdated:
      return buildAttachmentChangeConfig(
        attachmentClassificationOptions ?? [],
        attachmentTypeOptions ?? attachmentModuleEnumOptions
      );
    default:
      return null;
  }
};
